import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import qs from 'qs';
import { Observable, Subject, concatMap, map, of, tap } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { EventLogin } from '@enums/enum';
import { UserProfile } from '@interfaces/schema';
import { MaybeNull } from '@interfaces/types';
import { AuthPageQueryParams } from '@pages/auth/auth.interface';
import { EVENT } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';
import { AuthActions } from './auth.actions';
import { AuthService } from './auth.service';

/**
 * Auth Component.
 *
 * @exports
 * @class AuthComponent
 */
@Component({
  selector: 'app-auth',
  template: ``,
  standalone: true,
  providers: [AuthService]
})
export class AuthComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly event = inject(EventService);
  private readonly nav = inject(NavController);
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(AuthService);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  private readonly params = signal<{
    redirectTo?: string;
  }>({});
  private readonly token = signal<string | null>(null);

  destroyed = new Subject<boolean>();

  /**
   * Init data.
   */
  ngOnInit(): void {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('auth') ?? params.get('token');
    if (token) {
      this.token.set(token);
      this.session.set(token);
    }

    this.route.queryParams
      .pipe(takeUntil(this.destroyed))
      .subscribe((queryParams: Params) => {
        this.params.set(queryParams);
      });

    this.fetch();
  }

  /**
   * Init subscriptions.
   *
   * @memberof AuthComponent
   */
  ngAfterViewInit(): void {
    this.subscribes();
  }

  /**
   * Init data.
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  /**
   * Event listener.
   *
   * @memberof AuthComponent
   */
  subscribes(): void {
    this.event
      .listen(EVENT.LOGIN)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set session.
         *
         * @param {MaybeNull<EventLogin>} value Payload.
         */
        next: (value: MaybeNull<EventLogin>) => {
          if (value?.token) {
            if (value.authType) {
              this.session.store('auth-type', value.authType);
            }
            this.session.set(value.token);
            this.fetch(true);
          }
        }
      });

    this.event
      .listen(EVENT.FETCH_PROFILE)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set session.
         */
        next: () => {
          this.fetch(false);
        }
      });
    this.event
      .listen(EVENT.PROFILE_UPDATED)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set session.
         */
        next: () => {
          this.store.dispatch(AuthActions.profileUpdated());
          this.fetch(false);
        }
      });

    this.event
      .listen(EVENT.LOGOUT)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * @description  Clear session.
         * @param {object} payload Payload.
         */
        next: (payload) => {
          const redirect: boolean = get(payload, 'redirect', true);
          if (redirect) {
            this.nav
              .navigateRoot(['/auth/login'], {
                replaceUrl: true,
                queryParams: {
                  forceAccess: '1'
                } as AuthPageQueryParams,
                queryParamsHandling: 'replace',
                animated: true
              })
              .then(() => {
                this.store.dispatch(AuthActions.logout());
              })
              .catch(() => {
                this.store.dispatch(AuthActions.logout());
              });
          } else {
            this.store.dispatch(AuthActions.logout());
          }
        }
      });
  }

  /**
   * Fetch data.
   *
   * @param {boolean} redirect Redirect page.
   */
  fetch(redirect?: boolean): void {
    of(true)
      .pipe(
        // if user not authenticated, skip fetching data process.
        takeWhile(() => this.session.isAuthenticated())
      )
      .pipe(
        concatMap(() => this.service.profile()),
        concatMap((profile) => {
          if (profile) return this.setTimezone(profile);
          return of(profile);
        })
      )
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        /**
         * Handle response.
         *
         * @param {object} profile Profile data.
         */
        next: (profile) => {
          if (profile) {
            this.store.dispatch(AuthActions.setProfile({ profile }));
            // check redirect route from deeplinks
            const {
              to: redirectTo = this.params().redirectTo,
              queryParams
            }: any = this.session.retrieve('redirect') ?? {};

            const complete =
              this.service.isBasicPersonalInformationFilled(profile);
            // start notes.
            // Before 3.2.15, we skip greeting page for apple sso users
            // The greeting page was containing very basic personal information such as
            // full name and dob, which usually already provided by apple.
            // But now, we have to collect more personal information,
            // so we cannot skip greeting page for apple sso users.
            // The code below is kept here and just commented out for possible future reference.
            // const skipGreeting =
            //   /apple/i.test(authType) && SKIP_GREETING_FOR_SSO_APPLE;
            // if (complete || skipGreeting)
            // end notes.

            const token = this.token();
            if (token) {
              // bypass redirect if there is token in the url
              // this is intended for magic link purpose only
            } else {
              if (complete) {
                if (redirect) {
                  if (redirectTo) {
                    this.session.clear('redirect');
                    this.redirect(redirectTo, queryParams);
                  } else {
                    this.redirect('/home');
                  }
                }
              } else {
                if (redirectTo) {
                  this.session.clear('redirect');

                  const queryParamsString: string = qs.stringify(queryParams, {
                    arrayFormat: 'repeat',
                    skipNulls: true
                  });

                  this.redirect('/greetings/personal-information', {
                    redirectTo: [redirectTo, queryParamsString]
                      .filter((v) => Boolean(v))
                      .join('?')
                  });
                } else {
                  this.redirect('/greetings/personal-information', {
                    redirectTo: '/questionnaire/general'
                  });
                }
              }
            }
          } else {
            this.event.publish(EVENT.LOGOUT, { redirect: false });

            const path = window.location.pathname;
            const search = window.location.search;
            const params = { back: false };

            let redirectTo = undefined;

            if (path !== '/' && !/auth/i.test(path)) {
              redirectTo = path;
              if (search) {
                redirectTo = redirectTo.concat(search);
              }
            }

            if (redirectTo) {
              Object.assign(params, { redirectTo });
            }

            this.redirect('/auth/login', params);
          }
        },
        /**
         * Handle error response.
         *
         * @param {unknown} error Error response.
         */
        error: (error) => {
          this.event.publish(EVENT.TOAST, error);
        }
      });
  }

  /**
   * Redirect to url. (after redirect, the previous page cannot accessable again).
   *
   * @param {string} url Redirect url.
   * @param {object} queryParams QueryParams.
   */
  redirect(url: string, queryParams?: Record<string, any>): void {
    this.nav.navigateRoot(url, {
      queryParams,
      replaceUrl: true,
      onSameUrlNavigation: 'reload',
      animationDirection: 'forward',
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Set timezone.
   *
   * @param {object} profile Profile data.
   * @returns {Observable<UserProfile>}
   * @memberof AuthComponent
   */
  setTimezone(profile: UserProfile): Observable<UserProfile> {
    return of(true).pipe(
      map(() => DateTime.now().zoneName || 'Asia/Jakarta'),
      concatMap((timezone) => {
        if (profile.timezone) return of(profile);
        return this.service.edit({ form: { timezone } });
      }),
      tap((res) => {
        this.session.store('timezone', res.timezone ?? 'Asia/Jakarta');
      }),
      concatMap(() => of(profile))
    );
  }
}
